
import React from 'react';
import { Assessment, Category } from '@/types/assessment';
import AssessmentWelcome from './AssessmentWelcome';

import QuestionRenderer from './QuestionRenderer';
import PersonalInfoForm from './PersonalInfoForm';
import AssessmentComplete from './AssessmentComplete';
import { ResponseData } from '@/hooks/useAssessmentResponses';

interface AssessmentContentProps {
  currentStep: 'welcome' | 'questions' | 'personalInfo' | 'complete';
  assessment: Assessment;
  categories: Category[];
  currentCategoryIndex: number;
  currentQuestionIndex: number;
  responses: Record<string, ResponseData>;
  scoresByCategory: Record<string, {current: number, target: number}>;
  personalInfo: {
    fullName: string;
    email: string;
    company: string;
    designation: string;
    consentGiven: boolean;
  };
  onStart: () => void;
  onRestart: () => void;
  onExit: () => void;
  onBack: () => void;
  onNext: () => void;
  onResponseChange: (questionId: string, current: string[] | number, target: string[] | number) => void;
  onPersonalInfoSubmit: (info: any) => Promise<void>;
  canMoveForward: boolean;
  isSubmitting?: boolean;
  isPremium?: boolean;
}

const AssessmentContent = ({
  currentStep,
  assessment,
  categories,
  currentCategoryIndex,
  currentQuestionIndex,
  responses,
  scoresByCategory,
  personalInfo,
  onStart,
  onRestart,
  onExit,
  onBack,
  onNext,
  onResponseChange,
  onPersonalInfoSubmit,
  canMoveForward,
  isSubmitting = false,
  isPremium = false
}: AssessmentContentProps) => {
  
  const currentCategory = categories[currentCategoryIndex];
  const currentQuestion = currentCategory?.questions[currentQuestionIndex];
  const currentResponse = currentQuestion ? responses[currentQuestion.id] : undefined;

  switch (currentStep) {
    case 'welcome':
      return (
        <AssessmentWelcome
          assessment={assessment}
          onStart={onStart}
        />
      );


    case 'questions':
      // Safety check to ensure question exists before rendering
      if (!currentQuestion) {
        return (
          <div className="p-6 text-center">
            <p className="text-muted-foreground">Question not found. Please try again.</p>
            <button onClick={onBack} className="mt-4 px-4 py-2 bg-primary text-white rounded">
              Go Back
            </button>
          </div>
        );
      }

      return (
        <QuestionRenderer
          question={currentQuestion}
          response={currentResponse}
          onChange={onResponseChange}
          onBack={onBack}
          onNext={onNext}
          canMoveForward={canMoveForward}
          isLastQuestion={false}
        />
      );

    case 'personalInfo':
      return (
        <PersonalInfoForm
          initialValues={personalInfo}
          onSubmit={onPersonalInfoSubmit}
          onBack={onBack}
          isSubmitting={isSubmitting}
        />
      );

    case 'complete':
      return (
        <AssessmentComplete
          assessment={assessment}
          categories={categories}
          scoresByCategory={scoresByCategory}
          responses={responses}
          onRestart={onRestart}
          onExit={onExit}
          isPremium={isPremium}
        />
      );

    default:
      return null;
  }
};

export default AssessmentContent;
